import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import { enGB, nl } from 'date-fns/locale'

const LANGUAGES = {
  en: {
    locale: 'en-GB',
    dateLocale: enGB,
  },
  nl: {
    locale: 'nl-NL',
    dateLocale: nl,
  },
} as const

type Language = keyof typeof LANGUAGES

const REGIONS = {
  uk: {
    languages: ['en'],
    timezone: 'Europe/London',
    currency: 'GBP',
  },
  nl: {
    languages: ['nl'],
    timezone: 'Europe/Amsterdam',
    currency: 'EUR',
  },
} as const

type Region = keyof typeof REGIONS

const DEFAULT_REGION: Region = 'nl'

function resolveRegion(value: unknown): Region {
  if (typeof value === 'string' && value in REGIONS) {
    return value as Region
  }

  if (value) {
    console.warn(`Invalid TMC_REGION "${String(value)}"; defaulting to "${DEFAULT_REGION}"`)
  }

  return DEFAULT_REGION
}

const configuredRegion = typeof window === 'undefined' ? undefined : window._env_?.TMC_REGION
const { languages, timezone, currency } = REGIONS[resolveRegion(configuredRegion)]
const supportedLanguages: readonly Language[] = languages
const defaultLanguage = supportedLanguages[0]

function activeLanguage(): Language {
  const language = i18next.resolvedLanguage
  return supportedLanguages.includes(language as Language) ? language as Language : defaultLanguage
}

const formatting = {
  timezone,
  currency,
  get locale() {
    return LANGUAGES[activeLanguage()].locale
  },
  get dateLocale() {
    return LANGUAGES[activeLanguage()].dateLocale
  },
}

export { formatting }

const resources: Record<string, Record<string, object>> = {}
for (const [path, mod] of Object.entries(
  import.meta.glob<{ default: object }>('./locales/*/*.json', { eager: true }),
)) {
  const [lng, file] = path.split('/').slice(-2)
  const namespace = file.replace('.json', '')
  const languageResources = (resources[lng] ??= {})

  languageResources[namespace] = mod.default
}

void i18next.use(initReactI18next).init({
  resources,
  lng: defaultLanguage,
  fallbackLng: defaultLanguage,
  supportedLngs: supportedLanguages,
  load: 'languageOnly',
  defaultNS: 'common',
  fallbackNS: 'common',
  // React escapes rendered text itself; escaping here too shows a name like D'Hondt as D&#39;Hondt.
  interpolation: { escapeValue: false },
})

// index.html is prerendered without a runtime region, so its <html lang> is
// baked as the default — and React does not patch attribute mismatches while
// hydrating, so root.tsx cannot fix it. Set it here.
if (typeof document !== 'undefined') document.documentElement.lang = defaultLanguage

export default i18next
